/**
 * WEBE-26603 — fires the experiment exposure event once, client-side, after the
 * server-rendered variant hydrates. Renders nothing.
 */
import { useEffect } from 'react';
import { trackExposure } from '@/utils/ab/exposure';
import { CONSENT_EVENT, hasAbConsent } from '@/utils/ab/consent';

// Module-level, not a ref: the page subtree remounts whenever `_app` re-renders (`withSession` builds a new
// component type per render), and a ref would reset on each remount and re-fire the exposure. Keyed by
// experiment + variant so a different variant shown later in the same visit is still recorded.
const fired = new Set<string>();

export const AbExposure = ({ experimentKey, variant }: { experimentKey: string; variant: string }) => {
  useEffect(() => {
    const key = `${experimentKey}:${variant}`;
    // Consent is checked at send time, not left to Amplitude: nothing is queued before opt-in (so an exposure
    // can't flush later, on another page, for a bucket that was never stored), and nothing is sent after
    // withdrawal even though the Amplitude SDK stays live. (Experiment only renders this for a visitor with a
    // bucket, and the bucket cookie only exists after consent.)
    const fire = () => {
      if (fired.has(key) || !hasAbConsent()) {
        return;
      }
      fired.add(key);
      trackExposure({ experimentKey, variant });
    };

    fire();
    window.addEventListener(CONSENT_EVENT, fire);
    return () => window.removeEventListener(CONSENT_EVENT, fire);
  }, [experimentKey, variant]);

  return null;
};
